import {
  contrastRatio,
  oklchToRgbInGamut,
  parseHex,
  rgbToOklch,
  toHex,
  type Rgb,
} from './color.js';
import { CHASSIS } from './tokens.js';

/**
 * The palette derivation and the WCAG contrast gate.
 *
 * BUILD_PLAN 9.3 and the design handoff both specify the behaviour precisely:
 * when a chosen team color cannot meet AA as text against paper, say so in
 * plain language and offer the nearest compliant shade FOR TEXT, while keeping
 * the true color for FILLS.
 *
 * That last clause is the whole design. A coach whose team wears yellow is not
 * told their color is wrong, and a parent is not handed unreadable text. Both
 * outcomes are available only because text and fill are allowed to differ.
 */

export const PALETTE_STEPS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950] as const;
export type PaletteStep = (typeof PALETTE_STEPS)[number];

/** Target OKLCH lightness per step. Tuned so 500 lands on the seed color. */
const STEP_LIGHTNESS: Readonly<Record<PaletteStep, number>> = {
  50: 0.97,
  100: 0.94,
  200: 0.87,
  300: 0.79,
  400: 0.7,
  500: 0.6,
  600: 0.52,
  700: 0.44,
  800: 0.36,
  900: 0.28,
  950: 0.2,
};

export interface DerivedPalette {
  readonly seed: string;
  readonly steps: Readonly<Record<PaletteStep, string>>;
}

/**
 * Derive an 11-step ramp from a seed color.
 *
 * Hue is held constant and chroma is eased toward zero at the extremes, because
 * a fully saturated near-white or near-black reads as a printing error rather
 * than as the team's color.
 */
export function derivePalette(seedHex: string): DerivedPalette | null {
  const seed = parseHex(seedHex);
  if (seed === null) return null;

  const { c, h } = rgbToOklch(seed);
  const steps = {} as Record<PaletteStep, string>;

  for (const step of PALETTE_STEPS) {
    const l = STEP_LIGHTNESS[step];
    // Taper chroma at both ends of the ramp. The multiplier peaks in the middle
    // where the color should be most itself.
    const distanceFromMid = Math.abs(l - 0.6) / 0.6;
    const chroma = c * (1 - 0.55 * distanceFromMid * distanceFromMid);
    steps[step] = toHex(oklchToRgbInGamut({ l, c: chroma, h }));
  }

  return { seed: toHex(seed), steps };
}

export const AA_NORMAL_TEXT = 4.5;
export const AA_LARGE_TEXT = 3;
/** WCAG 1.4.11, non-text contrast, for borders and interactive boundaries. */
export const AA_NON_TEXT = 3;

export interface ContrastVerdict {
  readonly ratio: number;
  readonly passesAA: boolean;
  readonly passesAALarge: boolean;
  readonly passesNonText: boolean;
}

export function checkContrast(
  foregroundHex: string,
  backgroundHex: string,
): ContrastVerdict | null {
  const fg = parseHex(foregroundHex);
  const bg = parseHex(backgroundHex);
  if (fg === null || bg === null) return null;

  const ratio = contrastRatio(fg, bg);
  return {
    ratio,
    passesAA: ratio >= AA_NORMAL_TEXT,
    passesAALarge: ratio >= AA_LARGE_TEXT,
    passesNonText: ratio >= AA_NON_TEXT,
  };
}

export interface TextShadeResolution {
  /** The color as chosen. Always preserved for fills, whatever the verdict. */
  readonly fill: string;
  /** The shade safe to set type in. Equals `fill` when the seed already passes. */
  readonly text: string;
  readonly seedRatio: number;
  readonly textRatio: number;
  /** True when the seed itself failed and a different shade was substituted. */
  readonly adjusted: boolean;
}

/**
 * Find the nearest shade of a color that meets AA as text against a background.
 *
 * Walks OKLCH lightness away from the seed in small steps, preferring the
 * direction that increases contrast, and keeps hue and chroma so the result
 * still reads as the team's color rather than as a generic dark gray.
 *
 * Never fails: if no shade of the hue can pass (which happens for a very light
 * seed on paper only at extreme chroma), it falls back to `--ink`, so the
 * interface always has something readable to render.
 */
export function resolveTextShade(
  seedHex: string,
  backgroundHex: string = CHASSIS.paper,
): TextShadeResolution | null {
  const seed = parseHex(seedHex);
  const background = parseHex(backgroundHex);
  if (seed === null || background === null) return null;

  const seedRatio = contrastRatio(seed, background);
  if (seedRatio >= AA_NORMAL_TEXT) {
    return {
      fill: toHex(seed),
      text: toHex(seed),
      seedRatio,
      textRatio: seedRatio,
      adjusted: false,
    };
  }

  const { c, h } = rgbToOklch(seed);
  const backgroundIsLight = contrastRatio(background, { r: 0, g: 0, b: 0 }) > 10.5;

  let best: { color: Rgb; ratio: number } | null = null;
  for (let step = 1; step <= 100; step += 1) {
    // No bounds guard on `l`. The walk ends at 0 against a light background and
    // at 1 against a dark one, and the gamut mapper handles both ends without
    // clipping. A guard here would only add a branch nothing can reach.
    const l = backgroundIsLight ? 0.6 - step * 0.006 : 0.6 + step * 0.004;
    const candidate = oklchToRgbInGamut({ l, c, h });
    const ratio = contrastRatio(candidate, background);
    if (ratio >= AA_NORMAL_TEXT) {
      best = { color: candidate, ratio };
      break;
    }
  }

  // Unreachable in practice, and provably so: for a shade to be needed at all
  // the seed must fail, and the walk runs to near-black against a light
  // background or near-white against a dark one. The lightness bands where
  // black fails AA and where white fails AA do not overlap, so one end always
  // clears 4.5:1. Kept as a guarantee that the interface always has something
  // readable to render rather than deleted on the strength of that argument.
  /* c8 ignore start */
  if (best === null) {
    const ink = parseHex(CHASSIS.ink);
    if (ink === null) return null;
    return {
      fill: toHex(seed),
      text: CHASSIS.ink,
      seedRatio,
      textRatio: contrastRatio(ink, background),
      adjusted: true,
    };
  }
  /* c8 ignore stop */

  return {
    fill: toHex(seed),
    text: toHex(best.color),
    seedRatio,
    textRatio: best.ratio,
    adjusted: true,
  };
}

/**
 * Every custom property the theme injector writes.
 *
 * Exported so the consuming stylesheet can be checked against it rather than
 * trusted to agree. `apps/web` asserts that each of these is declared in
 * `tokens.css`, which is the test that would have caught the injector emitting
 * six names nothing read.
 *
 * Note what is NOT here: paper, ink, rule, and the semantic colors. Rubric C7
 * says semantic colors are never derived from team colors, and keeping the
 * written set to this list is how that stays checkable by reading one array.
 */
export const THEME_VARIABLE_NAMES: readonly string[] = [
  '--color-team-primary',
  '--color-team-accent',
  '--color-team-primary-text',
  '--color-team-accent-text',
  '--color-team-primary-on-fill',
  '--color-team-accent-on-fill',
  // The derived ramps. These were emitted and declared nowhere, so
  // `bg-primary-600` generated no utility and rendered as nothing. Same failure
  // class as the six above and found in the same review, which is why the list
  // is exhaustive rather than a summary.
  ...PALETTE_STEPS.map((step) => `--color-primary-${String(step)}`),
  ...PALETTE_STEPS.map((step) => `--color-accent-${String(step)}`),
];

export interface OnFillResolution {
  /** The color to set type in, on top of the given fill. */
  readonly color: string;
  readonly ratio: number;
  readonly passesAA: boolean;
}

/**
 * The readable text color to sit ON a team-colored fill.
 *
 * The counterpart to `resolveTextShade`, which answers the other question: that
 * one is for team-colored type on paper, this one is for type on a team-colored
 * button. Both are needed and neither substitutes for the other.
 *
 * This gap was found while building the primitives. `themeCssVariables` emitted
 * shades for type on paper and nothing for type on a fill, so a component had
 * to hardcode white. That is correct for the sample scarlet and wrong for the
 * handoff's own pale yellow example, where white on `#F2D33C` is unreadable and
 * `--ink` is fine.
 *
 * Chooses between the two chassis values rather than inventing a third, because
 * a button whose label is a slightly different near-black on every team is a
 * chassis that moves, and the whole design thesis is a chassis that does not.
 *
 * `passesAA` can be false for a genuinely awkward mid-tone fill where neither
 * ink nor paper reaches 4.5:1. The theme editor should say so plainly rather
 * than the caller pretending otherwise, which is why the ratio is returned.
 */
export function resolveOnFill(fillHex: string): OnFillResolution | null {
  const fill = parseHex(fillHex);
  const ink = parseHex(CHASSIS.ink);
  const paper = parseHex(CHASSIS.paper);
  if (fill === null || ink === null || paper === null) return null;

  const inkRatio = contrastRatio(ink, fill);
  const paperRatio = contrastRatio(paper, fill);
  const useInk = inkRatio >= paperRatio;
  const ratio = useInk ? inkRatio : paperRatio;

  return {
    color: useInk ? CHASSIS.ink : CHASSIS.paper,
    ratio,
    passesAA: ratio >= AA_NORMAL_TEXT,
  };
}

/**
 * The CSS custom property block injected at boot from the team record.
 *
 * Writes ONLY the team slots. It never touches the chassis or the semantic
 * colors, which is what makes rubric C7 checkable by reading this one function
 * instead of auditing every component.
 */
export function themeCssVariables(primaryHex: string, accentHex: string): string | null {
  const primary = derivePalette(primaryHex);
  const accent = derivePalette(accentHex);
  const primaryText = resolveTextShade(primaryHex);
  const accentText = resolveTextShade(accentHex);
  const primaryOnFill = resolveOnFill(primaryHex);
  const accentOnFill = resolveOnFill(accentHex);
  if (
    primary === null ||
    accent === null ||
    primaryText === null ||
    accentText === null ||
    primaryOnFill === null ||
    accentOnFill === null
  ) {
    return null;
  }

  const lines: string[] = [
    // The `--color-` prefix is not decoration: it is Tailwind v4's convention,
    // and under ADR-0003 a `@theme` entry IS the custom property the generated
    // utilities read. `bg-team-primary` compiles to
    // `background-color: var(--color-team-primary)`.
    //
    // These briefly shipped as `--team-primary`, which meant the injector wrote
    // six variables that nothing on earth read: every utility stayed on the
    // placeholder and applying a team theme did visibly nothing. Caught by the
    // cross-model review. THEME_VARIABLE_NAMES below exists so the next person
    // to rename one has a list to check against, and apps/web asserts that list
    // against the real stylesheet.
    `--color-team-primary: ${primary.seed};`,
    `--color-team-accent: ${accent.seed};`,
    `--color-team-primary-text: ${primaryText.text};`,
    `--color-team-accent-text: ${accentText.text};`,
    // Type sitting ON the fill, as opposed to the two above, which are team
    // colored type sitting on paper.
    `--color-team-primary-on-fill: ${primaryOnFill.color};`,
    `--color-team-accent-on-fill: ${accentOnFill.color};`,
  ];
  for (const step of PALETTE_STEPS) {
    lines.push(`--color-primary-${String(step)}: ${primary.steps[step]};`);
  }
  for (const step of PALETTE_STEPS) {
    lines.push(`--color-accent-${String(step)}: ${accent.steps[step]};`);
  }

  return `:root {\n  ${lines.join('\n  ')}\n}`;
}
